import { useEffect, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  Flex as ChakraFlex,
  Box as ChakraBox,
  Button as ChakraButton,
} from '@chakra-ui/react';
import { useMoveOutreachMutation } from '@/store/query/contacts';
import { ErrorTableWithReason as InnerErrorTableWithReason } from './DeleteError';
import { ChevronRight } from '@kula-ai/icons-core';
import { KText } from '@/ui-library/components/text';
import { useShowToast } from '@/hooks/useShowToast';

// Chakra polymorphic unions trigger TS2590 when nested deeply (Modal > Flex >
// Box > Button etc.). Cast at the import boundary — same workaround used in
// StageActivities.tsx and other heavy-Chakra files.
const Flex = ChakraFlex as any;
const Box = ChakraBox as any;
const Button = ChakraButton as any;
const ErrorTableWithReason = InnerErrorTableWithReason as any;

interface MoveFlowErrorsProps {
  isOpen?: boolean;
  onClose?: () => void;
  onCloseParent?: () => void;
  errors?: any;
  targetOutreachId?: any;
  allCandidateIds?: any[];
}

export const MoveFlowErrors = ({
  isOpen = false,
  onClose = () => {},
  onCloseParent = () => {},
  errors = [],
  targetOutreachId,
  allCandidateIds = [],
}: MoveFlowErrorsProps) => {
  const toast = useShowToast();
  const [tabIndex, setTabIndex] = useState(0);
  const [selectedIds, setSelectedIds] = useState([]);
  const [elidibleCandidates, setElidibleCandidates] = useState([]);

  const { invalid_candidates, previously_unsubscribed_candidates } = (errors ||
    {}) as {
    invalid_candidates?: any[];
    previously_unsubscribed_candidates?: any[];
  };
  const [moveOutreach, { isLoading }] = useMoveOutreachMutation();
  const isUnsubscribed = tabIndex === 0;
  const is2Steps =
    invalid_candidates?.length && previously_unsubscribed_candidates?.length;

  useEffect(() => {
    const combinedCandidates = [
      ...(invalid_candidates ?? []),
      ...(previously_unsubscribed_candidates ?? []),
    ].map((candidate) => candidate?.id);

    if (combinedCandidates?.length) {
      const filteredCandidates = allCandidateIds.filter(
        (id) => !combinedCandidates.includes(id)
      );
      setElidibleCandidates(filteredCandidates);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invalid_candidates, previously_unsubscribed_candidates]);

  const handleClose = () => {
    onClose();
    onCloseParent();
  };

  const handleActiveClose = async () => {
    if (
      !is2Steps &&
      !!invalid_candidates?.length &&
      elidibleCandidates?.length
    ) {
      await moveOutreach({
        ids: elidibleCandidates,
        toOutreach: targetOutreachId,
        toRefetch: false,
      });
      toast({
        description: 'Update completed',
        position: 'bottom-left',
        status: 'success',
      });
    }
    handleClose();
  };

  const handleAddToFlow = async (type = 'two_step') => {
    if (elidibleCandidates?.length || selectedIds?.length) {
      await moveOutreach({
        ids: [...selectedIds, ...elidibleCandidates],
        toOutreach: targetOutreachId,
        toRefetch: false,
      });
      toast({
        description: 'Update completed',
        position: 'bottom-left',
        status: 'success',
      });
    }
    if (type === 'two_step') {
      setTabIndex(1);
    } else {
      handleClose();
    }
  };

  const renderActiveCandidates = () => {
    return (
      <>
        <Flex px="20px" mt="30px" mb="18px" flexDirection="column" gap="5px">
          <KText fontWeight="semiBold" fontSize="16px">
            {`Note: ${invalid_candidates?.length} candidate${
              invalid_candidates?.length > 1 ? 's' : ''
            } could not be moved`}
          </KText>
          <KText>
            They are engaged in an active flow, are already part of this flow or
            are active in the connected job.
          </KText>
        </Flex>
        <Box
          overflowY="auto"
          overflowX="hidden"
          maxH="300px"
          m="12px 24px"
          borderRadius="md"
          border="1px solid"
          borderColor="border-color"
          onWheel={(e: any) => e.stopPropagation()}
          onTouchMove={(e: any) => e.stopPropagation()}
        >
          <ErrorTableWithReason selected={invalid_candidates} />
        </Box>
        <Flex px="20px" justifyContent="flex-end">
          <Button onClick={handleActiveClose}>Understood</Button>
        </Flex>
      </>
    );
  };

  const renderUnsubscribedCandidates = (type = 'two_step') => {
    return (
      <>
        <Flex px="20px" mt="30px" mb="18px" flexDirection="column" gap="5px">
          <KText fontWeight="semiBold" fontSize="16px">
            {`Note: ${previously_unsubscribed_candidates?.length} candidate${
              previously_unsubscribed_candidates?.length > 1 ? 's' : ''
            } have previously unsubscribed from another flow`}
          </KText>
          <KText>Select the candidates you want to add to this Flow</KText>
        </Flex>
        <Box
          overflowY="auto"
          overflowX="hidden"
          maxH="300px"
          m="12px 24px"
          borderRadius="md"
          border="1px solid #E5E5E5"
          onWheel={(e: any) => e.stopPropagation()}
          onTouchMove={(e: any) => e.stopPropagation()}
        >
          <ErrorTableWithReason
            selected={previously_unsubscribed_candidates}
            isUnsubscribed
            handleSelectedIds={(ids) => setSelectedIds(ids)}
          />
        </Box>
        <Flex px="20px" justifyContent="flex-end" gap="6px">
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button isLoading={isLoading} onClick={() => handleAddToFlow(type)}>
            Add to flow
          </Button>
        </Flex>
      </>
    );
  };

  const renderStepWizard = () => {
    return (
      <Flex alignItems="center" gap="5px" justifyContent="center" mt="24px">
        <Flex gap="6px" alignItems="center" cursor="not-allowed">
          <KText
            w="24px"
            h="24px"
            borderRadius="50%"
            bg={isUnsubscribed ? '#5a54ff2e' : '#F2F2F2'}
            color={isUnsubscribed && '#5A54FF'}
            textAlign="center"
            pt="1px"
          >
            1
          </KText>
          <KText
            fontSize="sm"
            color={isUnsubscribed && '#5A54FF'}
            fontWeight="medium"
          >
            Unsubscribed
          </KText>
        </Flex>
        <ChevronRight size={15} color="#464547" className="kula-icon" />
        <Flex gap="6px" alignItems="center" cursor="not-allowed">
          <KText
            w="24px"
            h="24px"
            borderRadius="50%"
            bg={!isUnsubscribed ? '#5a54ff2e' : '#F2F2F2'}
            color={!isUnsubscribed && '#5A54FF'}
            textAlign="center"
            pt="1px"
          >
            2
          </KText>
          <KText
            fontSize="sm"
            color={!isUnsubscribed && '#5A54FF'}
            fontWeight="medium"
          >
            Not moved
          </KText>
        </Flex>
      </Flex>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="3xl"
      isCentered
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent p="8px">
        {is2Steps ? (
          <>
            {renderStepWizard()}
            {isUnsubscribed
              ? renderUnsubscribedCandidates()
              : renderActiveCandidates()}
          </>
        ) : (
          <>
            {!!previously_unsubscribed_candidates?.length &&
              renderUnsubscribedCandidates('one_step')}
            {!!invalid_candidates?.length && renderActiveCandidates()}
          </>
        )}
      </ModalContent>
    </Modal>
  );
};
